import { showError, showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'

import { pushQueryParams, redirectTo } from 'actions/core/navigation'
import { setWorkflowFromApi, setWorkflowLoadError } from 'features/workflows'
import { idSelector } from 'selectors/current_user'
import {
  workflowsOwnerSelector
} from 'selectors/workflows'
import {
  apiOwnerEventTypesUrl, apiWorkflowShowUrl,
  apiOwnerListUrl, apiWorkflowEditUrl, apiWorkflowNewUrl,
  apiOwnerListLegacyUrl, appWorkflowListUrl
} from 'services/workflows/url'

export const fetchEventTypes = (pageToLoad) => (
  async (dispatch, getState) => {
    dispatch({
      type: 'WORKFLOWS_EVENT_TYPES_REQUEST'
    })

    const owner = workflowsOwnerSelector(getState())

    const [success, responseData] = await httpRequest({
      url: apiOwnerEventTypesUrl,
      method: 'GET',
      params: {
        page: pageToLoad,
        scope: owner.type === 'page' ? 'page_single' : 'owned_event_types',
        owner_id: owner.id,
        owner_type: owner.type,
      }
    })

    if (success) {
      dispatch({
        type: 'WORKFLOWS_EVENT_TYPES_DONE',
        payload: responseData
      })
    } else {
      dispatch({
        type: 'WORKFLOWS_EVENT_TYPES_FAIL'
      })
      dispatch(showHttpError(responseData))
    }
    return success
  }
)

const fetchOwnersApiRequest = (params, { workflowsExtractionEnabled }) => httpRequest({
  url: workflowsExtractionEnabled ? apiOwnerListUrl : apiOwnerListLegacyUrl,
  method: 'GET',
  params
})

export const fetchOwners = (params, { workflowsExtractionEnabled }) => (
  async (dispatch) => {
    dispatch({
      type: 'WORKFLOWS_OWNER_LIST_REQUEST'
    })

    const [success, responseData] = await fetchOwnersApiRequest(params, { workflowsExtractionEnabled })

    if (success) {
      dispatch({
        type: 'WORKFLOWS_OWNER_LIST_DONE',
        payload: responseData
      })
    } else {
      dispatch({
        type: 'WORKFLOWS_OWNER_LIST_FAIL'
      })
      dispatch(showHttpError(responseData))
    }

    return success
  }
)

const fetchMyCalendlyOwnersPage1 = ({ adminManaged, workflowsExtractionEnabled } = {}) => (
  async (dispatch, getState) => {
    const currentUserId = idSelector(getState())
    dispatch({
      type: 'WORKFLOWS_OWNER_LIST_REQUEST'
    })

    const requests = adminManaged
      ? [
        fetchOwnersApiRequest({
          owner_type: 'user',
          owner_id: currentUserId,
          admin_managed: true
        }, { workflowsExtractionEnabled })]
      : [
        fetchOwnersApiRequest({
          owner_type: 'user',
          owner_id: currentUserId
        }, { workflowsExtractionEnabled }),
        fetchOwnersApiRequest({
          owner_type: 'team',
          membership: {
            user_id: currentUserId
          },
          page_size: 8
        }, { workflowsExtractionEnabled })
      ]

    const responses = await Promise.all(requests)
    const success = responses.every(([requestSuccessful]) => requestSuccessful)

    if (success) {
      const collection = responses.reduce((acc, [, { collection: workflows }]) => [...acc, ...workflows], [])
      const [, { pagination }] = adminManaged ? responses[0] : responses[1]

      dispatch({
        type: 'WORKFLOWS_OWNER_LIST_DONE',
        payload: {
          collection,
          pagination
        }
      })
    } else {
      dispatch({
        type: 'WORKFLOWS_OWNER_LIST_FAIL'
      })

      const [status, data] = responses.find(([requestSuccessful, responseData]) => requestSuccessful || responseData)
      dispatch(showHttpError(status || data))
    }

    return success
  }
)

const fetchMyCalendlyOwnersPageN = ({ page, workflowsExtractionEnabled }) => (
  async (dispatch, getState) => {
    const currentUserId = idSelector(getState())
    dispatch({
      type: 'WORKFLOWS_OWNER_LIST_REQUEST'
    })

    const [success, response] = await fetchOwnersApiRequest({
      owner_type: 'team',
      membership: {
        user_id: currentUserId
      },
      page_number: page,
      page_size: 8
    }, { workflowsExtractionEnabled })

    if (success) {
      const lastPage = response.pagination.total_pages || 1
      if (page > lastPage) {
        dispatch(pushQueryParams({ page: lastPage }))
      } else {
        dispatch({
          type: 'WORKFLOWS_OWNER_LIST_DONE',
          payload: response
        })
      }
    } else {
      dispatch({
        type: 'WORKFLOWS_OWNER_LIST_FAIL'
      })

      dispatch(showHttpError(response))
    }

    return success
  }
)

export const fetchMyCalendlyOwners = ({ page, adminManaged, workflowsExtractionEnabled }) => (
  async (dispatch) => {
    if (page === '1' || page === 1) {
      dispatch(fetchMyCalendlyOwnersPage1({ adminManaged, workflowsExtractionEnabled }))
      return
    }

    dispatch(fetchMyCalendlyOwnersPageN({ page, workflowsExtractionEnabled }))
  }
)

const fetchWorkflowShowApiRequest = (owner, workflowUuid, workflowsExtractionEnabled) => {
  const url = workflowsExtractionEnabled
    ? `/api/apps/workflows/workflows/${workflowUuid}`
    : apiWorkflowShowUrl(owner, workflowUuid, workflowsExtractionEnabled)
  return httpRequest({
    url,
    method: 'GET'
  })
}

const fetchWorkflowEditApiRequest = async (owner, workflowUuid, workflowsExtractionEnabled) => {
  if (workflowsExtractionEnabled) {
    const [workflowHttp, ownerHttp] = await Promise.all([
      httpRequest({ url: `/api/apps/workflows/workflows/${workflowUuid}` }),
      httpRequest({
        url: '/api/apps/workflows/send_options',
        params: { owner_type: owner.type, owner_id: owner.id }
      })
    ])
    const [workflowSuccess, workflowResponse] = workflowHttp
    if (!workflowSuccess) return [false, workflowResponse]

    const [ownerSuccess, ownerResponse] = ownerHttp
    if (!ownerSuccess) return [false, ownerResponse]

    return [
      true,
      { builder: { workflow: workflowResponse }, owner: ownerResponse.owner }
    ]
  }
  return httpRequest({ url: apiWorkflowEditUrl(owner, workflowUuid), method: 'GET' })
}


export const fetchWorkflowForEdit = (owner, workflowUuid, workflowsExtractionEnabled) => (
  async (dispatch) => {
    const [success, response] = await fetchWorkflowEditApiRequest(owner, workflowUuid, workflowsExtractionEnabled)

    if (!success) {
      dispatch(showHttpError(response))
      if (response.type === 'policy') {
        dispatch(redirectTo(appWorkflowListUrl(owner, false)))
      } else {
        dispatch(setWorkflowLoadError())
      }
      return
    }

    dispatch(setWorkflowFromApi({
      ...response.builder.workflow,
    }))
    dispatch({ type: 'WORKFLOWS_OWNER_SET', payload: { owner: response.owner } })
  }
)

export const fetchWorkflowForShow = (owner, workflowUuid, workflowsExtractionEnabled) => (
  async (dispatch) => {
    const [success, response] = await fetchWorkflowShowApiRequest(owner, workflowUuid, workflowsExtractionEnabled)

    if (!success) {
      dispatch(showHttpError(response))
      dispatch(setWorkflowLoadError())
      return
    }

    dispatch(setWorkflowFromApi({
      ...response
    }))

    dispatch({ type: 'WORKFLOWS_OWNER_SET', payload: { owner } })
  }
)

const fetchWorkflowNewApiRequest = (owner, workflowsExtractionEnabled) => {
  if (workflowsExtractionEnabled) {
    return httpRequest({
      url: '/api/apps/workflows/send_options',
      method: 'GET',
      params: {
        owner_type: owner.type,
        owner_id: owner.id
      }
    })
  }

  return httpRequest({
    url: apiWorkflowNewUrl(owner),
    method: 'GET'
  })
}

export const fetchWorkflowForNew = (owner, workflowsExtractionEnabled) => (
  async (dispatch) => {
    const [success, response] = await fetchWorkflowNewApiRequest(owner, workflowsExtractionEnabled)

    if (!success) {
      if (response.type === 'policy') {
        dispatch(showError(I18n.t('errors.policy.cannot_perform_this_action')))
        dispatch(redirectTo(appWorkflowListUrl(owner, false)))
      } else {
        dispatch(showHttpError(response))
        dispatch({ type: 'WORKFLOWS_OWNER_SET', payload: { owner } })
      }
      return
    }

    dispatch({ type: 'WORKFLOWS_OWNER_SET', payload: { owner: response.owner } })
  }
)
